import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, LogOut, Download, Loader2, ShieldCheck, Rocket, Mail, Trash2, RefreshCw, ArrowLeft, Search, X, Bell, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hi-admin-token";

const field =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-2.5 text-sm text-white placeholder-white/40 hi-body focus:outline-none focus:border-[#3b9bff]/60 focus:ring-1 focus:ring-[#3b9bff]/40";

const TABS = [
  { key: "beta-signups", statKey: "beta_signups", label: "Beta Signups", icon: Rocket, cols: ["created_at", "name", "email", "apps", "platform"] },
  { key: "newsletter", statKey: "newsletter_subscribers", label: "Subscribers", icon: Mail, cols: ["created_at", "email", "interests", "beta_interest"] },
  { key: "deletion-requests", statKey: "deletion_requests", label: "Deletion Requests", icon: Trash2, cols: ["created_at", "name", "email", "app", "reason"] },
  { key: "launch-list", statKey: "launch_list", label: "Launch List", icon: Bell, cols: ["created_at", "first_name", "email", "app"] },
];
const ENDPOINT = { "beta-signups": "beta-signups", newsletter: "newsletter", "deletion-requests": "deletion-requests", "launch-list": "launch-list" };

const fmt = (c, v) => {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (c === "created_at") return new Date(v).toLocaleString();
  if (c === "beta_interest") return v ? "Yes" : "No";
  return String(v);
};

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back.");
      onLogin(data.token);
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="hi-card p-8 w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#3b9bff]/15 text-[#3b9bff] border border-[#3b9bff]/30"><Lock size={22} /></span>
          <h1 className="hi-serif text-2xl text-[#f3ead4] mt-4">Admin Sign In</h1>
          <p className="hi-body text-sm text-white/65 mt-1">Herrell Interactive dashboard</p>
        </div>
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="admin-login-form">
          <input data-testid="admin-email" type="email" className={field} placeholder="Email"
            value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={field} placeholder="Password"
            value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="hi-cta w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63] disabled:opacity-60">
            {loading ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={17} strokeWidth={2.5} />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <Link to="/" className="hi-body flex items-center justify-center gap-2 text-xs text-white/50 hover:text-white mt-5">
          <ArrowLeft size={13} /> Back to site
        </Link>
      </motion.div>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("beta-signups");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState({ col: "created_at", dir: "desc" });

  const authFetch = useCallback(
    async (path) => {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onLogout(); throw new Error("Session expired"); }
      return res;
    },
    [token, onLogout]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        authFetch(`/admin/${ENDPOINT[tab]}`),
        authFetch(`/admin/stats`),
      ]);
      setRows(await r.json());
      setStats(await s.json());
    } catch { /* handled */ } finally { setLoading(false); }
  }, [authFetch, tab]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await authFetch(`/admin/export/${tab}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${tab}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch { toast.error("Export failed."); }
  };

  const exportAll = async () => {
    try {
      const res = await authFetch(`/admin/export-all`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `herrell-interactive-export.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("All lists exported.");
    } catch { toast.error("Export failed."); }
  };

  const active = TABS.find((t) => t.key === tab);

  const filtered = rows.filter((r) => {
    if (q.trim()) {
      const hay = active.cols
        .map((c) => (Array.isArray(r[c]) ? r[c].join(" ") : String(r[c] ?? "")))
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    if (from || to) {
      const t = r.created_at ? new Date(r.created_at).getTime() : 0;
      if (from && t < new Date(from + "T00:00:00").getTime()) return false;
      if (to && t > new Date(to + "T23:59:59").getTime()) return false;
    }
    return true;
  });

  const clearFilters = () => { setQ(""); setFrom(""); setTo(""); };

  const toggleSort = (col) =>
    setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));

  const sorted = [...filtered].sort((a, b) => {
    const norm = (v) => (Array.isArray(v) ? v.join(", ") : v == null ? "" : v);
    let x = norm(a[sort.col]), y = norm(b[sort.col]);
    if (sort.col === "created_at") { x = new Date(a[sort.col] || 0).getTime(); y = new Date(b[sort.col] || 0).getTime(); }
    else if (typeof x === "boolean" || typeof y === "boolean") { x = x ? 1 : 0; y = y ? 1 : 0; }
    else { x = String(x).toLowerCase(); y = String(y).toLowerCase(); }
    if (x < y) return sort.dir === "asc" ? -1 : 1;
    if (x > y) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const filterInput = "rounded-lg bg-white/5 border border-white/15 px-3 py-2 text-sm text-white placeholder-white/40 hi-body focus:outline-none focus:border-[#3b9bff]/60";

  return (
    <div className="max-w-6xl mx-auto px-5 py-8 md:py-12">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <img src="/favicon.png" alt="Herrell Interactive" className="w-9 h-9 rounded-md" />
          <div>
            <h1 className="hi-serif text-2xl text-[#f3ead4]">Admin Inbox</h1>
            <p className="hi-body text-xs text-white/55">Beta signups · subscribers · deletion requests</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="admin-refresh" className="hi-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hi-body bg-white/5 border border-white/10 text-white/80 hover:bg-white/10">
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={onLogout} data-testid="admin-logout" className="hi-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hi-body bg-white/5 border border-white/10 text-white/80 hover:bg-white/10">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`stat-${t.key}`}
            className={`hi-card p-5 text-left transition-colors ${tab === t.key ? "ring-1 ring-[#3b9bff]/60" : ""}`}>
            <div className="flex items-center gap-2 text-white/60"><t.icon size={16} /><span className="hi-body text-xs uppercase tracking-wider">{t.label}</span></div>
            <div className="hi-serif text-3xl text-[#f3ead4] mt-2">{stats[t.statKey] ?? "—"}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mt-10">
        <div className="flex-1 min-w-[200px]">
          <label className="hi-body text-[11px] uppercase tracking-wider text-white/50 mb-1 block">Search</label>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="admin-search"
              className={filterInput + " w-full pl-9"}
              placeholder="Search name, email, app…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="hi-body text-[11px] uppercase tracking-wider text-white/50 mb-1 block">From</label>
          <input data-testid="admin-from" type="date" className={filterInput} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="hi-body text-[11px] uppercase tracking-wider text-white/50 mb-1 block">To</label>
          <input data-testid="admin-to" type="date" className={filterInput} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {(q || from || to) && (
          <button data-testid="admin-clear-filters" onClick={clearFilters} className="hi-cta inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm hi-body bg-white/5 border border-white/10 text-white/70 hover:bg-white/10">
            <X size={14} /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="flex items-center justify-between mt-6 mb-4">
        <h2 className="hi-serif text-xl text-[#f3ead4] flex items-center gap-2">
          <active.icon size={18} className="text-[#3b9bff]" /> {active.label}
          <span data-testid="admin-count" className="hi-body text-sm text-white/50 font-normal">({filtered.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={exportAll} data-testid="admin-export-all" className="hi-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hi-body bg-white/5 border border-white/10 text-white/80 hover:bg-white/10">
            <Download size={15} /> Export All
          </button>
          <button onClick={exportCsv} data-testid="admin-export" className="hi-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63]">
            <Download size={15} strokeWidth={2.5} /> Export CSV
          </button>
        </div>
      </div>

      <div className="hi-card overflow-x-auto" data-testid="admin-table">
        {loading ? (
          <div className="p-10 text-center text-white/60"><Loader2 className="animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center hi-body text-white/60" data-testid="admin-empty">
            {rows.length === 0 ? "No records yet." : "No records match your filters."}
          </div>
        ) : (
          <table className="w-full text-left border-collapse min-w-[640px]">
            <thead>
              <tr className="border-b border-white/10">
                {active.cols.map((c) => (
                  <th
                    key={c}
                    data-testid={`sort-${c}`}
                    onClick={() => toggleSort(c)}
                    className="hi-body text-[11px] uppercase tracking-wider text-white/55 font-semibold px-5 py-3 cursor-pointer select-none hover:text-white"
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.replace("_", " ")}
                      {sort.col === c
                        ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                        : <ChevronsUpDown size={12} className="opacity-40" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={i} data-testid="admin-row" className="border-b border-white/[0.06] hover:bg-white/[0.03]">
                  {active.cols.map((c) => (
                    <td key={c} className="hi-body text-sm text-white/80 px-5 py-3 align-top">{fmt(c, r[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };

  return (
    <div className="hi-bg hi-grain min-h-screen" data-testid="admin-page">
      <div className="relative z-[2]">
        {token ? <Dashboard token={token} onLogout={logout} /> : <LoginView onLogin={setToken} />}
      </div>
    </div>
  );
};

export default Admin;
